import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Empty from 'antd/lib/empty';
import Input from 'antd/lib/input';
import message from 'antd/lib/message';
import Select from 'antd/lib/select';
import Table from 'antd/lib/table';
import Tag from 'antd/lib/tag';
import Button from 'antd/lib/button';
import FooterBar from '../components/helpdesk/FooterBar';
import HeaderBar from '../components/helpdesk/HeaderBar';
import StatsCards from '../components/knowledge-base/StatsCards';
import { listArticles } from '../api/articles';
import { mapApiArticleToCard } from '../api/articles.mappers';
import '../styles/helpdesk.scss';

function buildOptions(values, allLabel) {
  return [
    { value: 'all', label: allLabel },
    ...Array.from(new Set(values.filter(Boolean))).map((item) => ({ value: item, label: item })),
  ];
}

function PublicArticlesPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState('all');
  const [categoryOptions, setCategoryOptions] = useState([{ value: 'all', label: 'All Categories' }]);
  const [tagOptions, setTagOptions] = useState([{ value: 'all', label: 'All Tags' }]);

  const loadArticles = useCallback(async () => {
    setLoading(true);
    try {
      const response = await listArticles({
        page: 1,
        pageSize: 200,
        status: 'PUBLISHED',
      });
      const mapped = response.items.map(mapApiArticleToCard).filter((item) => item.status === 'Published');
      setRows(mapped);
      setCategoryOptions(buildOptions(mapped.map((item) => item.category), 'All Categories'));
      setTagOptions(buildOptions(mapped.flatMap((item) => item.tags || []), 'All Tags'));
    } catch {
      message.error('Unable to load public articles.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadArticles();
  }, [loadArticles]);

  const visibleRows = useMemo(() => {
    return rows.filter((item) => {
      const searchOk =
        !searchTerm.trim() ||
        [item.title, item.description, item.author, item.category, ...(item.tags || [])]
          .join(' ')
          .toLowerCase()
          .includes(searchTerm.trim().toLowerCase());
      const categoryOk = categoryFilter === 'all' || item.category === categoryFilter;
      const tagOk = tagFilter === 'all' || (item.tags || []).includes(tagFilter);
      return searchOk && categoryOk && tagOk;
    });
  }, [rows, searchTerm, categoryFilter, tagFilter]);

  const stats = useMemo(() => {
    return [
      { id: 'total', label: 'Published Articles', value: visibleRows.length, tone: 'success' },
      { id: 'categories', label: 'Categories', value: categoryOptions.length - 1, tone: 'default' },
      { id: 'tags', label: 'Tags', value: tagOptions.length - 1, tone: 'warning' },
    ];
  }, [categoryOptions.length, tagOptions.length, visibleRows.length]);

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (_, record) => (
        <button
          type="button"
          className="kb-table__title"
          onClick={() => navigate(`/articles/${encodeURIComponent(record.slug || record.id)}`)}
          title={record.title}
        >
          <span>{record.title}</span>
        </button>
      ),
    },
    { title: 'Category', dataIndex: 'category', key: 'category', width: 170, ellipsis: true },
    {
      title: 'Tags',
      dataIndex: 'tags',
      key: 'tags',
      width: 220,
      render: (tags) =>
        tags?.length ? (
          <span className="kb-table__tags">
            <Tag key={tags[0]}>{tags[0]}</Tag>
            {tags.length > 1 ? <Tag className="kb-table__tag-overflow">+{tags.length - 1}</Tag> : null}
          </span>
        ) : (
          '-'
        ),
    },
    { title: 'Author', dataIndex: 'author', key: 'author', width: 170, ellipsis: true },
    { title: 'Date', dataIndex: 'date', key: 'date', width: 130 },
  ];

  return (
    <div className="helpdesk-page">
      <HeaderBar />
      <main className="helpdesk-main">
        <div className="helpdesk-main__inner">
          <section className="kb-page" aria-labelledby="public-kb-title">
            <div className="kb-page__header">
              <div>
                <h1 id="public-kb-title">Knowledge Base</h1>
                <p>Browse published help articles</p>
              </div>
            </div>

            <section className="kb-page__overview" aria-label="Public knowledge overview">
              <div className="kb-page__overview-copy">
                <h2>Public Article Library</h2>
                <p>Search and explore published documentation and troubleshooting guides.</p>
              </div>
              <div className="kb-page__overview-meta">
                <span>{visibleRows.length} article(s)</span>
              </div>
            </section>

            <div className="kb-page__workspace">
              <StatsCards stats={stats} />

              <section className="kb-articles" aria-label="Public articles table">
                <div className="kb-articles__header">
                  <h2>All Articles</h2>
                  <p>{visibleRows.length} total</p>
                </div>

                <div className="kb-articles__toolbar kb-articles__toolbar--public">
                  <Input.Search
                    placeholder="Search articles..."
                    enterButton={false}
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                  />
                  <Select value={categoryFilter} options={categoryOptions} onChange={setCategoryFilter} />
                  <Select value={tagFilter} options={tagOptions} onChange={setTagFilter} />
                </div>

                {visibleRows.length || loading ? (
                  <>
                    <Table
                      className="kb-table"
                      rowKey={(record) => record.id}
                      columns={columns}
                      dataSource={visibleRows}
                      scroll={{ x: 1000 }}
                      loading={loading}
                      pagination={{ pageSize: 10 }}
                    />
                    <div className="kb-table-mobile" aria-label="Public articles mobile list">
                      {visibleRows.map((article) => (
                        <article key={article.id} className="kb-mobile-card">
                          <div className="kb-mobile-card__head">
                            <h3 className="kb-mobile-card__title-text">{article.title}</h3>
                          </div>
                          <p className="kb-mobile-card__text">{article.description || '-'}</p>
                          <div className="kb-mobile-card__meta">
                            <span>{article.category || '-'}</span>
                            <span>{article.author || '-'}</span>
                            <span>{article.date || '-'}</span>
                          </div>
                          <div className="kb-mobile-card__actions">
                            <Button
                              size="small"
                              onClick={() => navigate(`/articles/${encodeURIComponent(article.slug || article.id)}`)}
                            >
                              Open Article
                            </Button>
                          </div>
                        </article>
                      ))}
                    </div>
                  </>
                ) : (
                  <Empty description="No published articles found" />
                )}
              </section>
            </div>
          </section>
        </div>
      </main>
      <FooterBar />
    </div>
  );
}

export default PublicArticlesPage;
